import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BuildIcon from "@mui/icons-material/Build";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../../services/api";
import { Add, Search } from "@mui/icons-material";

const initialForm = {
  code: "",
  name: "",
  description: "",
  status: "ACTIVE",
};

export default function ShopMaster() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });
  const rowsPerPage = isMobile ? 5 : 10;

  const loadRows = async () => {
    try {
      setLoading(true);

      const response = await api.get("/shops");

      console.log("Shop API response:", response.data);

      // Handle:
      // { success: true, data: [...] }
      // OR
      // [...]
      let shopData = [];

      if (Array.isArray(response.data)) {
        shopData = response.data;
      } else if (Array.isArray(response.data?.data)) {
        shopData = response.data.data;
      }

      setRows(shopData);
    } catch (error) {
      console.error("loadRows:", error);

      setRows([]);

      setMessage({
        open: true,
        type: "error",
        text: error.response?.data?.message || "Failed to load shops",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) {
      return [];
    }

    const value = search.trim().toLowerCase();

    if (!value) {
      return rows;
    }

    return rows.filter((item) => {
      const code = String(item?.code ?? "").toLowerCase();
      const name = String(item?.name ?? "").toLowerCase();
      const description = String(item?.description ?? "").toLowerCase();
      const status = String(item?.status ?? "").toLowerCase();

      return (
        code.includes(value) ||
        name.includes(value) ||
        description.includes(value) ||
        status.includes(value)
      );
    });
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

  const visibleRows = useMemo(() => {
    if (!Array.isArray(filteredRows)) {
      return [];
    }

    const startIndex = (page - 1) * rowsPerPage;

    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    setPage((currentPage) => {
      if (currentPage > pageCount) {
        return pageCount;
      }

      return currentPage;
    });
  }, [pageCount]);

  const openAdd = () => {
    setEditingId(null);
    setForm(initialForm);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code || "",
      name: item.name || "",
      description: item.description || "",
      status: item.status || "ACTIVE",
    });
    setOpen(true);
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    const code = form.code.trim().toUpperCase();
    const name = form.name.trim();

    if (!code || !name) {
      setMessage({
        open: true,
        type: "error",
        text: "Code and name are required",
      });
      return;
    }

    const payload = {
      code,
      name,
      description: form.description.trim() || null,
      status: form.status,
    };

    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/shops/${editingId}`, payload);
      } else {
        await api.post("/shops", payload);
      }
      setOpen(false);
      setMessage({
        open: true,
        type: "success",
        text: editingId
          ? "Shop updated successfully"
          : "Shop created successfully",
      });
      await loadRows();
    } catch (error) {
      console.error(error);
      setMessage({
        open: true,
        type: "error",
        text: error.response?.data?.message || "Save failed",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/shops/${deleteId}`);
      setDeleteId(null);
      setMessage({
        open: true,
        type: "success",
        text: "Shop deleted successfully",
      });
      await loadRows();
    } catch (error) {
      console.error(error);
      setDeleteId(null);
      setMessage({
        open: true,
        type: "error",
        text: error.response?.data?.message || "Delete failed",
      });
    }
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0, p: { xs: 1.5, sm: 2.5 } }}>
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BuildIcon sx={{ fontSize: 30, color: "primary.main" }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Shop Master
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage workshop shops used for coach repair and movement.
            </Typography>
          </Box>
        </Box>
        {/* <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Shop
        </Button> */}
      </Box>

      <Card
        sx={{
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          borderRadius: 2.5,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          

          
            <Box
                    sx={{
                      p: 2,
          
                      display: "flex",
          
                      gap: 2,
          
                      justifyContent: "space-between",
          
                      alignItems: {
                        xs: "stretch",
                        sm: "center",
                      },
          
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                      },
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search by code, name, location or status..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{
                        width: {
                          xs: "100%",
                          sm: 380,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
          
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent={{
                        xs: "space-between",
                        sm: "flex-end",
                      }}
                    >
                      <Chip label={`${filteredRows.length} Records`} variant="outlined" />
          
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={openAdd}
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Add Shop
                      </Button>
                    </Stack>
                  </Box>
          
          

          {isMobile ? (
            <Stack spacing={1.5}>
              {visibleRows.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700}>{item.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Code: {item.code}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, wordBreak: "break-word" }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={item.status}
                      color={item.status === "ACTIVE" ? "success" : "default"}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={0.5}
                    sx={{ mt: 1 }}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(item)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteId(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer
              sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}
            >
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow
                    sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap" } }}
                  >
                    <TableCell width="120">Code</TableCell>
                    <TableCell>Shop Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell width="120">Status</TableCell>
                    <TableCell width="110" align="right">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {item.code}
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        {item.description || "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={
                            item.status === "ACTIVE" ? "success" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(item)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && visibleRows.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No shops found.
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingId ? "Edit Shop" : "Add Shop"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Shop Code"
              value={form.code}
              onChange={handleChange("code")}
              fullWidth
              required
              inputProps={{ maxLength: 30 }}
            />
            <TextField
              label="Shop Name"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={handleChange("status")}
              fullWidth
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : editingId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Shop?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this shop?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={message.open}
        autoHideDuration={3500}
        onClose={() => setMessage((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={message.type}
          variant="filled"
          onClose={() => setMessage((p) => ({ ...p, open: false }))}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
